// src/screens/App/FranchisePages/CoffeeSection.jsx
import React, { useRef } from 'react';
import { Box, Stack } from '@mui/material';
import { motion, useScroll, useTransform } from 'framer-motion';
import Images from '../../../utils/Images';

// ------- Small helpers (no TS, safe in .jsx) -------
const useParallaxY = (ref, strength = -0.12) => {
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  // maps 0..1 -> 0..(strength*40 px)
  return useTransform(scrollYProgress, [0, 1], [0, strength * 40]);
};

const ParallaxCard = ({ children, speed = -0.12, className = '' }) => {
  const ref = useRef(null);
  const y = useParallaxY(ref, speed);
  return (
    <motion.div ref={ref} style={{ y }} className={`parallaxCard ${className}`}>
      {children}
    </motion.div>
  );
};

const TiltCard = ({ children, maxTilt = 6, scale = 1.01, className = '' }) => {
  const ref = useRef(null);

  const handle = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;  // 0..1
    const y = (e.clientY - rect.top) / rect.height;  // 0..1
    const rx = (0.5 - y) * (maxTilt * 2);
    const ry = (x - 0.5) * (maxTilt * 2);
    el.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) scale(${scale})`;
  };

  const reset = () => {
    const el = ref.current;
    if (el) el.style.transform = 'perspective(900px) rotateX(0deg) rotateY(0deg) scale(1)';
  };

  return (
    <div
      ref={ref}
      className={`tiltCard ${className}`}
      onMouseMove={handle}
      onMouseLeave={reset}
    >
      {children}
    </div>
  );
};

// ------- Reveal animation variants -------
const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.5, ease: 'easeOut', delay: i * 0.08 }
  })
};
const container = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } };

// ------- Row (parallax + tilt + hover lift) -------
const CoffeeRow = ({ src, text, idx }) => (
  <ParallaxCard speed={-0.12}>
    <TiltCard maxTilt={5} className="coffeeTiltWrap">
      <motion.div
        className="coffeeRow"
        variants={fadeUp}
        custom={idx}
        whileHover={{ scale: 1.01, translateY: -2 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
      >
        <Stack direction="row" alignItems="center">
          <img
            src={src}
            alt="coffeeList"
            className="coffeeIconParallax"
            style={{ height: '100%', paddingRight: '1.5vh' }}
          />
          <p className="coffeeList">{text}</p>
        </Stack>
      </motion.div>
    </TiltCard>
  </ParallaxCard>
);

const CoffeeSection = () => {
  return (
    <section className='coffeeSection'>
      {/* Titles */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.25 }}
        variants={container}
      >
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <motion.h1 className='timeTitle' variants={fadeUp}>Timing Is Everything.</motion.h1>
          <motion.h1 className='timeTitle' variants={fadeUp}>Time to invest in Coffee Is Now.</motion.h1>
          <motion.p className='timePara' variants={fadeUp}>India's coffee boom isn't coming-it's here.</motion.p>
          <motion.p className='timePara' variants={fadeUp}>
            With rising demand and untapped potential, the time to enter is right now.
          </motion.p>
        </Box>

        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <motion.h1 className='timeTitle' variants={fadeUp}>Timing Is Everything.</motion.h1>
          <motion.h1 className='timeTitle' variants={fadeUp}>Time to invest in</motion.h1>
          <motion.h1 className='timeTitle' variants={fadeUp}>Coffee Is Now.</motion.h1>
          <motion.p className='timePara' variants={fadeUp}>
            India's coffee boom isn't coming-it's here.
            With rising demand and untapped potential, the time to enter is right now.
          </motion.p>
        </Box>
      </motion.div>

      {/* Why Coffee Now */}
      <ParallaxCard speed={-0.08}>
        <div className="whyCoffeeBox">
          <motion.h3
            className="whyCoffeeTitle underlineWipe"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            Why Coffee Now
          </motion.h3>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            variants={container}
          >
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <motion.p className="whyCoffeePara" variants={fadeUp}>
                Coffee is no longer niche-it's the <i>new daily essential</i>.
              </motion.p>
              <motion.p className="whyCoffeePara" variants={fadeUp}>
                From young professionals to college students, India's urban population is more coffee than ever.
              </motion.p>
            </Box>

            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <motion.p className="whyCoffeePara" variants={fadeUp}>
                Coffee is no longer niche-it's the <i>new daily essential</i>.From young professionals to college students, India's urban population is more coffee than ever.
              </motion.p>
            </Box>

            <Box className="coffeeListBox" paddingTop={2}>
              <CoffeeRow src={Images.p1_1} text="Coffee consumption in India is up 30% since 2018" idx={0} />
              <CoffeeRow src={Images.p1_2} text="Over 50% of India is under 30—young, aspirational, and coffee-ready" idx={1} />
              <CoffeeRow src={Images.p1_3} text="Cafe's now double as social and remote work hubs" idx={2} />
              <CoffeeRow src={Images.p1_4} text="Per capita coffee consumption is still 1/20th of global average = huge growth room" idx={3} />
            </Box>
          </motion.div>
        </div>
      </ParallaxCard>

      {/* The Coffee Gaps */}
      <ParallaxCard speed={-0.08}>
        <div className="whyCoffeeBox">
          <motion.h3
            className="whyCoffeeTitle underlineWipe"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            The Coffee Gaps
          </motion.h3>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            variants={container}
          >
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <motion.p className="whyCoffeePara" variants={fadeUp}>
                The market's hot-but the model is broken. Franchisees have a chance to fill real gaps in how India
              </motion.p>
              <motion.p className="whyCoffeePara" variants={fadeUp}>does coffee.</motion.p>
            </Box>

            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              <motion.p className="whyCoffeePara" variants={fadeUp}>
                The market's hot-but the model is broken. Franchisees have a chance to fill real gaps in how India does coffee.
              </motion.p>
            </Box>

            <Box className="coffeeListBox" paddingTop={2}>
              <CoffeeRow src={Images.p2_1} text="Long queues and slow service are still common" idx={0} />
              <CoffeeRow src={Images.p2_2} text="High prices (₹300+) block mass adoption" idx={1} />
              <CoffeeRow src={Images.p2_3} text="Inconsistent experience across outlets weakens loyalty" idx={2} />
              <CoffeeRow src={Images.p2_4} text="Lack of tech = missed efficiency, insights and customer delight" idx={3} />
            </Box>
          </motion.div>
        </div>
      </ParallaxCard>
    </section>
  );
};

export default CoffeeSection;
